import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FiArrowUpRight } from "react-icons/fi";
import Header from "../components/Header";
import Footer from "../components/Footer";
import HeroSection from "../components/HeroSection";
import { DEV_TOOL_ICON_MAP } from "../data/devToolIcons";
import { fetchProjects } from "../api";

// Portfolio: the admin-managed banner and body, then the work we have delivered as cards.
export default function PortfolioPage() {
  const [projects, setProjects] = useState(null);
  const [category, setCategory] = useState("");

  useEffect(() => {
    fetchProjects().then(setProjects).catch(() => setProjects([]));
  }, []);

  const categories = useMemo(() => [...new Set((projects || []).map((p) => p.category).filter(Boolean))], [projects]);
  const visible = (projects || []).filter((p) => !category || p.category === category);

  return (
    <>
      <Header />
      <div className="h-16" />
      <HeroSection slug="portfolio" fallbackTitle="Portfolio" />

      <section className="bg-navy-dark text-white py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="text-3xl md:text-4xl font-bold text-center">Our Work</h2>

          {categories.length > 1 && (
            <div className="flex flex-wrap justify-center gap-2 mt-8">
              {["", ...categories].map((c) => (
                <button
                  key={c || "all"}
                  onClick={() => setCategory(c)}
                  className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                    category === c ? "bg-brand-gradient text-white" : "bg-white/5 border border-navy-border text-gray-300 hover:border-accent-light hover:text-white"
                  }`}
                >
                  {c || "All"}
                </button>
              ))}
            </div>
          )}

          {projects === null ? (
            <p className="text-center text-gray-500 py-12">Loading...</p>
          ) : visible.length === 0 ? (
            <div className="text-center py-14 mt-10 rounded-xl bg-navy border border-navy-border">
              <p className="text-lg font-semibold">Projects coming soon</p>
              <p className="text-gray-400 mt-1">We are putting our latest work together - please check back shortly.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 mt-10">
              {visible.map((project, i) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: Math.min(i, 6) * 0.06 }}
                  whileHover={{ y: -6 }}
                  className="group rounded-xl bg-navy border border-accent-light/25 shadow-[0_0_15px_rgba(34,211,238,0.2)] overflow-hidden transition-all duration-300 hover:border-accent-light hover:shadow-[0_0_35px_rgba(34,211,238,0.5)]"
                >
                  <Link to={`/portfolio/${project.id}`} className="block">
                    <div className="relative bg-navy-dark overflow-hidden">
                      <img src={project.image} alt={project.title} className="w-full h-auto block transition-transform duration-500 group-hover:scale-105" />
                      {project.category && (
                        <span className="absolute top-3 left-3 px-3 py-1 rounded-full bg-navy/85 backdrop-blur text-xs font-semibold text-accent-light">{project.category}</span>
                      )}
                    </div>
                    <div className="p-5">
                      <h3 className="text-lg font-semibold group-hover:text-accent-light transition-colors flex items-center justify-between gap-2">
                        {project.title} <FiArrowUpRight className="shrink-0 opacity-60" />
                      </h3>
                      {project.client && <p className="text-xs text-gray-400 mt-1">Client: {project.client}</p>}
                      {project.summary && <p className="text-sm text-gray-300 mt-3 line-clamp-3">{project.summary}</p>}
                      <div className="flex flex-wrap gap-1.5 mt-4">
                        {(project.technologies || "").split(",").filter(Boolean).slice(0, 6).map((key) => {
                          const entry = DEV_TOOL_ICON_MAP[key];
                          if (!entry) return null;
                          const { Icon, label } = entry;
                          return (
                            <span key={key} title={label} className="flex items-center gap-1 px-2 py-1 rounded-md bg-white/5 border border-navy-border text-white/70 text-xs">
                              <Icon size={12} />
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </>
  );
}
